import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { IMAGES, SERVICES, PROCESS, USE_CASES, COMPANY, PROMO_PORTAL_URL } from "../lib/site";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Eyebrow, SectionHeader, CTAButton, FinalCTA, useSeo } from "../components/Shared";
import { Marquee } from "../components/Marquee";
import { ClientPortalLink } from "../components/ClientPortalLink";

const Hero = () => (
  <section className="relative flex min-h-[92vh] items-end overflow-hidden bg-brand-black pb-16 pt-32">
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={IMAGES.heroWarehouse}
        alt="MJG Promotions fulfillment warehouse with organized inventory racking"
        className="h-full w-full object-cover"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/80 to-brand-black/50" />
      <div className="absolute inset-0 grid-overlay opacity-30" />
    </div>

    <div className="relative mx-auto w-full max-w-7xl px-5">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        <Eyebrow>Fulfillment, branded merchandise & event support</Eyebrow>
      </motion.div>

      <h1 className="mt-6 text-[2.6rem] font-extrabold leading-[1.12] tracking-tight text-white sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.1}>Your Brand.</MaskedLine>
        <MaskedLine delay={0.22}>Your Events.</MaskedLine>
        <MaskedLine delay={0.34}>
          One <span className="text-brand-red">Trusted Partner.</span>
        </MaskedLine>
      </h1>

      <Reveal delay={0.6} className="mt-7 max-w-2xl">
        <p className="text-lg leading-relaxed text-white/75">
          From fulfillment and warehousing to custom apparel, promotional products, exhibits, and event
          services, MJG Promotions provides everything you need to bring your brand to life.
        </p>
      </Reveal>

      <Reveal delay={0.75} className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <Link
          to="/fulfillment-warehousing"
          data-testid="hero-explore-services"
          className="group inline-flex items-center gap-2 bg-brand-red px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-redhover"
        >
          Explore Our Services <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <Link
          to="/contact"
          data-testid="hero-contact"
          className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-white/5"
        >
          Contact Us
        </Link>
        <a
          href={PROMO_PORTAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-portal-link"
          className="group inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/5"
        >
          Promotional Products Catalog <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>
    </div>
  </section>
);

const FulfillmentStrip = () => (
  <section className="bg-brand-warm py-20 sm:py-28" data-testid="fulfillment-strip">
    <div className="mx-auto max-w-7xl px-5">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow="Our Primary Specialty"
            title="Fulfillment Expertise That Keeps Your Brand Moving"
            body="MJG stores, organizes, prepares, and ships the branded materials your teams rely on. From inventory and kitting to order fulfillment and event-specific distribution, we keep every asset accounted for and ready to go."
          />
          <CTAButton to="/fulfillment-warehousing" testId="strip-discuss-fulfillment" variant="dark" >
            Discuss Your Fulfillment Program
          </CTAButton>
          <div className="mt-8 overflow-hidden">
            <img src={IMAGES.warehouseTablet} alt="MJG team member checking inventory on a tablet in the warehouse" className="aspect-[4/3] w-full rounded-sm object-cover" loading="lazy" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <ol className="grid gap-px overflow-hidden rounded-sm border border-brand-line bg-brand-line sm:grid-cols-2">
            {PROCESS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08}>
                <li className="group h-full bg-white p-7 transition-colors hover:bg-brand-black">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold text-brand-red">{step.num}</span>
                    <step.icon className="h-6 w-6 text-brand-charcoal transition-colors group-hover:text-white" aria-hidden="true" />
                  </div>
                  <h3 className="mt-8 text-xl font-bold text-brand-black transition-colors group-hover:text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-gray transition-colors group-hover:text-white/70">{step.desc}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </div>
  </section>
);

const WhatWeDo = () => (
  <section className="bg-white py-20 sm:py-28" data-testid="what-we-do">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader eyebrow="What We Do" title="Everything Your Brand Needs — Under One Roof" />

      <div className="mt-12 grid gap-5 lg:grid-cols-12 lg:grid-rows-3">
        {/* Flagship card */}
        <Reveal className="lg:col-span-6 lg:row-span-3">
          <Link
            to={SERVICES[0].slug}
            data-testid="card-fulfillment"
            className="group relative flex h-full min-h-[420px] flex-col justify-end overflow-hidden rounded-sm bg-brand-black p-8"
          >
            <img src={IMAGES.warehouseAisle} alt="Organized warehouse racking for branded assets" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 group-hover:scale-105 group-hover:opacity-50" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/70 to-transparent" />
            <div className="absolute left-6 top-6 inline-flex items-center gap-2 bg-brand-red px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
              Flagship Service
            </div>
            <div className="relative">
              <h3 className="text-2xl font-extrabold text-white sm:text-3xl">Fulfillment & Warehousing</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">{SERVICES[0].short}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-white">
                Learn more <ArrowRight className="h-4 w-4 text-brand-red transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>

        {/* Secondary cards */}
        {SERVICES.slice(1).map((s, i) => {
          const inner = (
            <>
              <s.icon className="h-7 w-7 text-brand-red" aria-hidden="true" />
              <div className="mt-6">
                <h3 className="text-lg font-bold text-brand-black">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.short}</p>
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-black">
                Learn more <ArrowRight className="h-4 w-4 text-brand-red transition-transform group-hover:translate-x-1" />
              </span>
            </>
          );
          const cls = "group flex h-full flex-col justify-between rounded-sm border border-brand-line bg-brand-warm p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-red hover:bg-white hover:shadow-xl lg:col-span-3";
          return (
            <Reveal key={s.title} delay={i * 0.06} className="lg:col-span-3">
              {s.external ? (
                <ClientPortalLink testId="card-portal" className={`${cls} w-full text-left`}>{inner}</ClientPortalLink>
              ) : (
                <Link to={s.slug} data-testid={`card${s.slug}`} className={cls}>{inner}</Link>
              )}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyMJG = () => {
  const pillars = [
    { t: "Simplify", d: "One partner for your marketing and event needs." },
    { t: "Manage", d: "Track and access your branded assets through our cloud-based portal." },
    { t: "Deliver", d: "From production and storage to shipping and onsite support, we handle the details." },
  ];
  return (
    <section className="relative overflow-hidden bg-brand-black py-20 sm:py-28" data-testid="why-mjg">
      <div className="absolute inset-0 grid-overlay opacity-20" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5">
        <SectionHeader dark eyebrow="Why MJG" title="More Than a Vendor. An Extension of Your Team." body="We manage the details behind your brand so you don't have to. From storing and preparing your assets to shipping them to your next event, our team helps simplify the entire process." />
        <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.1}>
              <div className="h-full bg-brand-black p-8">
                <span className="font-mono text-sm text-brand-red">0{i + 1}</span>
                <h3 className="mt-5 text-2xl font-extrabold text-white">{p.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CloudPortal = () => {
  const features = [
    "View available inventory",
    "Submit orders and shipment requests",
    "Share approved assets across departments, teams, or locations",
    "Support optional approval workflows",
    "Maintain visibility into requests and destinations",
  ];
  return (
    <section className="bg-brand-warm py-20 sm:py-28" data-testid="cloud-portal">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHeader eyebrow="Cloud-Based Portal" title="Your Inventory. Your Portal. Your Brand." body="Keep your branded assets organized, accessible, and ready for your next event. Our cloud-based portal gives authorized team members visibility into inventory and makes ordering and fulfillment simple." />
            <ul className="mt-8 space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-brand-charcoal">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <ClientPortalLink testId="portal-section-cta" showIcon className="group inline-flex items-center gap-2 bg-brand-black px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-charcoal">
                Access Client Portal
              </ClientPortalLink>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="overflow-hidden rounded-sm border border-brand-line bg-brand-black shadow-2xl">
              <img src={IMAGES.portalDashboard} alt="Conceptual MJG client portal dashboard showing inventory levels and order tracking" className="w-full object-cover" loading="lazy" />
              <figcaption className="border-t border-white/10 px-4 py-2.5 text-center font-mono text-[11px] uppercase tracking-widest text-white/50">
                Conceptual portal mockup — final interface may vary
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Apparel = () => {
  const cats = ["Apparel", "Headwear", "Drinkware", "Bags", "Uniforms", "Kits", "Event Giveaways"];
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="apparel-section">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Apparel & Promotional Products" title="Put Your Brand in Their Hands" body="Create apparel and merchandise people will be proud to use and wear. MJG combines in-house apparel production with experienced promotional-product support for uniforms, employee programs, recruiting, events, gifts, and giveaways." />
            <div className="mt-6 flex flex-wrap gap-2">
              {cats.map((c) => (
                <span key={c} className="border border-brand-line bg-brand-warm px-3 py-1.5 text-xs font-semibold text-brand-charcoal">{c}</span>
              ))}
            </div>
            <CTAButton to="/promotional-products-apparel" testId="apparel-cta" variant="primary">Explore Apparel & Promotional Products</CTAButton>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:col-span-7">
            <Reveal className="col-span-1"><img src={IMAGES.merchFlatlay} alt="Branded corporate merchandise flatlay including apparel, drinkware and bags" className="aspect-[3/4] w-full rounded-sm object-cover" loading="lazy" /></Reveal>
            <div className="col-span-1 flex flex-col gap-4">
              <Reveal delay={0.1}><img src={IMAGES.apparelSweatshirt} alt="Person wearing branded apparel outdoors" className="aspect-square w-full rounded-sm object-cover" loading="lazy" /></Reveal>
              <Reveal delay={0.2}><img src={IMAGES.caps} alt="Branded embroidered cap detail" className="aspect-square w-full rounded-sm object-cover" loading="lazy" /></Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TradeShowTeaser = () => {
  const choices = [
    { t: "Custom Exhibits", d: "Design and fabrication for a brand-specific presence." },
    { t: "Rental Exhibits", d: "Flexible, reconfigurable options for changing footprints." },
    { t: "Portable Displays", d: "Banners, pop-ups, and practical modular elements." },
  ];
  return (
    <section className="relative overflow-hidden bg-brand-black py-20 sm:py-28" data-testid="tradeshow-teaser">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={IMAGES.exhibit} alt="" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black/90 to-brand-black" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5">
        <SectionHeader dark eyebrow="Trade Show Exhibits" title="Make an Impact on the Show Floor" body="From portable displays and flexible rentals to custom exhibits, MJG helps you choose, prepare, ship, install, and manage an exhibit solution built around your brand, schedule, goals, and budget." />
        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {choices.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <div className="group h-full border border-white/15 p-7 transition-colors hover:border-brand-red">
                <h3 className="text-xl font-bold text-white">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10">
          <CTAButton to="/trade-show-exhibits" testId="tradeshow-cta" variant="primary">Explore Exhibit Options</CTAButton>
        </div>
      </div>
    </section>
  );
};

const UseCases = () => (
  <section className="bg-brand-warm py-20 sm:py-28" data-testid="use-cases">
    <div className="mx-auto max-w-7xl px-5">
      <SectionHeader eyebrow="Where We Help" title="Built for Every Activation" />
      <div className="mt-12 grid gap-px overflow-hidden border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-4">
        {USE_CASES.map((u, i) => (
          <Reveal key={u} delay={(i % 4) * 0.05}>
            <div className="group flex h-full items-center justify-between bg-white p-6 transition-colors hover:bg-brand-red">
              <span className="text-base font-bold text-brand-black transition-colors group-hover:text-white">{u}</span>
              <span className="font-mono text-xs text-brand-red transition-colors group-hover:text-white/80">0{i + 1}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  useSeo(
    "MJG Promotions | Fulfillment, Branded Merchandise & Exhibits",
    "MJG Promotions provides fulfillment and warehousing, promotional products, apparel, trade show exhibits, marketing collateral, and event support from Fairfield, NJ."
  );
  return (
    <>
      <Hero />
      <FulfillmentStrip />
      <Marquee />
      <WhatWeDo />
      <WhyMJG />
      <CloudPortal />
      <Apparel />
      <TradeShowTeaser />
      <UseCases />
      <FinalCTA />
    </>
  );
}
